<template>
  <ContentCardSkeleton v-if="isLoading" data-test="card-skeleton" />
  <ion-card
    v-else
    href=""
    :button="true"
    @click.prevent="$emit('card-clicked', id)"
    data-test="section-card"
  >
    <ion-card-header>
      <ion-card-title>{{ $t(title) }} </ion-card-title>
      <span class="icon-wrapper"
        ><ion-icon :icon="icon" data-test="icon" aria-hidden="true"></ion-icon
      ></span>
    </ion-card-header>
    <ion-card-content>
      {{ $t(description) }}
    </ion-card-content>
    <AppButton
      v-if="buttonText"
      class="button"
      size="small"
      :type="buttonType"
      :label="$t(buttonText)"
      aria-hidden="true"
      :disabled="false"
      data-test="button"
      tabindex="-1"
    ></AppButton>
  </ion-card>
</template>

<script setup lang="ts">
import { defineProps } from "vue";
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
} from "@ionic/vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import ContentCardSkeleton from "@/components/Skeletons/ContentCardSkeleton/ContentCardSkeleton.vue";
import type { Button } from "@/components/Common/AppButton/AppButton.vue";
interface Props {
  id: string;
  title: string;
  icon: string;
  description: string;
  buttonText: string;
  buttonType: Button;
  isLoading: boolean;
}
defineProps<Props>();
</script>

<style lang="scss" scoped>
ion-card {
  --ripple-color: transparent;
  -webkit-box-shadow: none;
  align-items: flex-start;
  background-color: var(--background-white);
  border-radius: 24px;
  border: 2px solid var(--light-grey-border);
  box-shadow: none;
  display: flex;
  flex-direction: column;
  height: 200px;
  margin: var(--spacing-0);
  max-width: 584px;
  padding: var(--spacing-24);
  &::part(native) {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: stretch;
  }
  &:focus {
    border: 2px solid var(--ion-color-secondary);
  }
  &:hover {
    box-shadow: 0px 7px 8px 0px var(--shadow-color-3),
      0px 5px 22px 0px var(--shadow-color-1),
      0px 12px 17px 0px var(--shadow-color-2);
  }
  &:hover .secondary,
  &:focus .secondary {
    //hover over button when hovering over the card
    box-shadow: none;
    &::part(native) {
      background: var(--ion-color-secondary-hover);
    }
  }
  &:hover .tertiary,
  &:focus .tertiary {
    //hover over button when hovering over the card
    box-shadow: none;
    &::part(native) {
      background: var(--ion-color-tertiary-hover);
    }
  }
  &:active {
    border: 2px solid var(--light-grey-border);
    box-shadow: none;
  }
  &:active .secondary {
    &::part(native) {
      background: var(--background-white);
    }
  }
  &:active .tertiary {
    &::part(native) {
      background: var(--background-white);
    }
  }
  &.ion-activated {
    --webkit-transform: none;
    transform: none;
  }
}

ion-card-header {
  padding: var(--spacing-0);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-8);
}

ion-card-title {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: center;
  color: var(--text-primary);
  display: -webkit-box;
  font-size: 22px;
  font-weight: 500;
  line-height: 28px;
  overflow: hidden;
}

ion-card-content {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: var(--text-secondary);
  display: -webkit-box;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 20px;
  overflow: hidden;
  padding: var(--spacing-0);
}
.icon-wrapper {
  background-color: var(--surface-lighter-grey);
  border-radius: 50%;
  display: inline-block;
  height: 40px;
  width: 40px;
}

ion-icon {
  color: var(--text-primary);
  font-size: 24px;
  padding: var(--spacing-8);
}

ion-button,
button {
  margin: var(--spacing-0);
  align-self: flex-start;
  &:hover {
    --box-shadow: none;
  }
  &:active {
    --ripple-color: transparent;
    background-color: var(--background-white);
  }
}
.button:last-of-type {
  margin-top: auto;
}
</style>
